// Helper function to get current currency
function getCurrentCurrency() {
  return window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR';
}

// Helper function to get locale prefix from current URL
// Returns the locale prefix (e.g., '/nl' for Dutch) or empty string for default language
function getLocalePrefix() {
  const pathname = window.location.pathname;
  // Extract locale prefix (e.g., /nl from /nl/cart)
  // Common locale codes are 2-3 characters
  const localeMatch = pathname.match(/^\/([a-z]{2,3})(?:\/|$)/i);
  if (localeMatch && localeMatch[1] !== 'cart' && localeMatch[1] !== 'checkout' && localeMatch[1] !== 'account') {
    return '/' + localeMatch[1];
  }
  return '';
}

// Helper function to build locale-aware URL
function getLocaleAwareUrl(path) {
  const localePrefix = getLocalePrefix();
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return localePrefix + normalizedPath;
}

// Map of selectors for all elements that must be refreshed when the cart updates.
const CART_ELEMENT_SELECTORS = {
  cartItemsContainer: '.cart-items-container',
  emptyCartMessage: '.empty-cart-message',
  cartSubtotal: '[data-cart-subtotal]',
  totalPrice: '[data-cart-total]',
  discounts: '[data-cart-discounts]'
};

// Builds a snapshot of every DOM element we care about so updates are centralized.
function mapCartElements() {
  const elementMap = {};
  Object.entries(CART_ELEMENT_SELECTORS).forEach(([key, selector]) => {
    elementMap[key] = document.querySelector(selector);
  });
  return elementMap;
}

const CartCalculations = {
  getTotalDiscountAmount(cart) {
    if (!cart) return 0;
    const cartLevelDiscounts = Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications.reduce((sum, discount) => {
           const amount = Number.isFinite(discount?.total_allocated_amount)
             ? discount.total_allocated_amount
             : 0;
           return sum + amount;
        }, 0)
      : 0;
    const lineLevelDiscounts = Array.isArray(cart.items)
      ? cart.items.reduce((sum, item) => {
           if (!Array.isArray(item.line_level_discount_allocations)) {
             return sum;
           }
           const itemTotal = item.line_level_discount_allocations.reduce(
             (innerSum, discount) => {
                const amount = Number.isFinite(discount?.amount) ? discount.amount : 0;
                return innerSum + amount;
             },
             0
           );
           return sum + itemTotal;
        }, 0)
      : 0;
    return cartLevelDiscounts + lineLevelDiscounts;
  }
};

// Cart Manager
class CartManager {
   constructor() {
      this.elements = mapCartElements();
      this.assignElementRefs();
      this.lastKnownCart = null;
      this.inFlightChange = null;
      this.isMutating = false;
      this.itemsMarkupRefreshInFlight = null;

      this.setupEventListeners();
      this.subscribeToCartUpdateEvents();

      // Initial hydrate from server to ensure fresh state
      this.refreshFromServer();
   }

   assignElementRefs() {
      const {
         cartItemsContainer,
         emptyCartMessage,
         cartSubtotal,
         totalPrice,
         discounts
      } = this.elements;
      this.cartItemsContainer = cartItemsContainer;
      this.emptyCartMessage = emptyCartMessage;
      this.cartSubtotal = cartSubtotal;
      this.totalPrice = totalPrice;
      this.discountRowsContainer = discounts;
   }

   refreshElementReferences() {
      this.elements = mapCartElements();
      this.assignElementRefs();
   }

   setupEventListeners() {
      document.addEventListener('click', (e) => {
         if (this.isMutating) {
            console.log('[CartDebug] Click ignored while mutating');
            return;
         }
         const decrementBtn = e.target.closest && e.target.closest('.quantity-decrement');
         const incrementBtn = e.target.closest && e.target.closest('.quantity-increment');

         if (!decrementBtn && !incrementBtn) return;

         const lineItem = (e.target.closest && e.target.closest('[data-line-item-key]')) || null;
         if (!lineItem) return;
         const key = lineItem.dataset.lineItemKey;
         const line = lineItem.dataset.line ? parseInt(lineItem.dataset.line) : null;

         const quantityInput = lineItem.querySelector('.quantity-input');
         const quantityText = lineItem.querySelector('.quantity-text');
         if (!quantityInput || !quantityText) return;
         const currentQty = parseInt(quantityInput.value);
         const newQty = decrementBtn ? currentQty - 1 : currentQty + 1;

         // Removing the last unit deletes the line item from the cart.
         if (newQty <= 0) {
            this.removeItem({ key, line }, lineItem);
            return;
         }

         // Optimistic UI
         quantityInput.value = newQty;
         quantityText.textContent = newQty;
         this.updateItemQuantity({ key, line }, newQty, lineItem);
      });
   }

   subscribeToCartUpdateEvents() {
      document.addEventListener('cart:updated', (event) => {
         const cart = event?.detail;
         if (cart && typeof cart.original_total_price === 'number') {
            this.updateCartDisplay(cart);
            return;
         }
         this.refreshFromServer();
      });
   }

   async fetchCart() {
      try {
         const response = await fetch(getLocaleAwareUrl('/cart.js'));
         const cart = await response.json();
         console.log('[CartDebug] fetchCart', {
            status: response.status,
            item_count: cart && cart.item_count,
            original_total_price: cart && cart.original_total_price,
            total_price: cart && cart.total_price,
            cart
         });
         return cart;
      } catch (error) {
         console.error('Error fetching cart:', error);
         return null;
      }
   }

   async refreshFromServer() {
      const cart = await this.fetchCart();
      if (cart) {
         this.updateCartDisplay(cart);
      }
   }

   setLineItemState(lineItem, className, active) {
      if (!lineItem) return;
      if (active) {
         lineItem.classList.add(className);
      } else {
         lineItem.classList.remove(className);
      }
   }

   async updateItemQuantity(ref, quantity, lineItem) {
      this.setLineItemState(lineItem, 'updating', true);
      this.isMutating = true;
      
      try {
         if (this.inFlightChange) {
            await this.inFlightChange.catch(() => {});
         }
         const body = ref && ref.line ? { line: ref.line, quantity } : { id: ref.key, quantity };
         console.log('[CartDebug] POST /cart/change.js body', body);
         this.inFlightChange = fetch(getLocaleAwareUrl('/cart/change.js'), {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json'
         },
         body: JSON.stringify(body)
         });
         const response = await this.inFlightChange;
         this.inFlightChange = null;
         if (!response.ok) {
            console.warn('[CartDebug] updateItemQuantity non-OK response', { status: response.status });
            await this.refreshFromServer();
            return;
         }
         const cart = await response.json();
         console.log('[CartDebug] updateItemQuantity response', {
            line: ref.line,
            key: ref.key,
            quantity,
            status: response.status,
            original_total_price: cart && cart.original_total_price,
            total_price: cart && cart.total_price,
            item_count: cart && cart.item_count,
            currency: getCurrentCurrency()
         });
         this.updateCartDisplay(cart);
         
      } catch (error) {
         console.error('Error updating quantity:', error);
         const quantityInput = lineItem.querySelector('.quantity-input');
         const quantityText = lineItem.querySelector('.quantity-text');
         if (quantityInput && quantityText) {
         const currentQty = parseInt(quantityInput.value);
         const revertedQty = currentQty > 1 ? currentQty - 1 : 1;
         quantityInput.value = revertedQty;
         quantityText.textContent = revertedQty;
         }
         await this.refreshFromServer();
      } finally {
         this.setLineItemState(lineItem, 'updating', false);
         this.isMutating = false;
      }
   }

   async removeItem(ref, lineItem) {
      this.setLineItemState(lineItem, 'removing', true);
      this.isMutating = true;
      
      try {
         if (this.inFlightChange) {
            await this.inFlightChange.catch(() => {});
         }
         const body = ref && ref.line ? { line: ref.line, quantity: 0 } : { id: ref.key, quantity: 0 };
         console.log('[CartDebug] POST /cart/change.js body', body);
         this.inFlightChange = fetch(getLocaleAwareUrl('/cart/change.js'), {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json'
         },
         body: JSON.stringify(body)
         });
         const response = await this.inFlightChange;
         this.inFlightChange = null;
         if (!response.ok) {
            console.warn('[CartDebug] removeItem non-OK response', { status: response.status });
            await this.refreshFromServer();
            return;
         }
         const cart = await response.json();
         console.log('[CartDebug] removeItem response', {
            line: ref.line,
            key: ref.key,
            status: response.status,
            original_total_price: cart && cart.original_total_price,
            total_price: cart && cart.total_price,
            item_count: cart && cart.item_count
         });
         
         if (lineItem && lineItem.parentNode) {
         lineItem.parentNode.removeChild(lineItem);
         }
         this.updateCartDisplay(cart);
         
      } catch (error) {
         console.error('Error removing item:', error);
         this.setLineItemState(lineItem, 'removing', false);
         await this.refreshFromServer();
      } finally {
         this.isMutating = false;
      }
   }

   buildDiscountRow(title, amount) {
      return `<div class="flex justify-between text-sm text-black">
         <p>${title}</p>
         <p>-${CurrencyUtils.formatMoney(amount)}</p>
      </div>`;
   }

   renderDiscountRows(cart) {
      if (!this.discountRowsContainer) return;
      const rows = [];
      const appendRow = (title, amount) => {
         if (!title || typeof amount !== 'number' || amount <= 0) return;
         rows.push(this.buildDiscountRow(title, amount));
      };

      const cartDiscounts = Array.isArray(cart.cart_level_discount_applications)
         ? cart.cart_level_discount_applications
         : [];
      cartDiscounts.forEach(discount => {
         appendRow(discount.title, discount.total_allocated_amount);
      });

      (cart.items || []).forEach(item => {
         const allocations = Array.isArray(item.line_level_discount_allocations)
            ? item.line_level_discount_allocations
            : [];
         allocations.forEach(discount => {
            appendRow(discount.discount_application?.title, discount.amount);
         });
      });

      this.discountRowsContainer.innerHTML = rows.join('') || '';
   }

   updateItemDiscountTags(cart) {
      if (!cart || !this.cartItemsContainer) return;
      const items = Array.isArray(cart.items) ? cart.items : [];
      items.forEach(item => {
         const lineElement = this.cartItemsContainer.querySelector(
            `[data-line-item-key="${item.key}"]`
         );
         if (!lineElement) return;
         const tag = lineElement.querySelector('.discountTag');
         if (!tag) return;
         const discountText =
            item.discounts?.[0]?.title ||
            item.line_level_discount_allocations?.[0]?.discount_application?.title ||
            '';
         if (discountText) {
            tag.textContent = discountText;
            tag.classList.remove('hidden');
         } else {
            tag.textContent = '';
            tag.classList.add('hidden');
         }
      });
   }

   refreshCartItemsMarkup(cart) {
      if (this.itemsMarkupRefreshInFlight) {
         return this.itemsMarkupRefreshInFlight;
      }
      if (typeof DOMParser === 'undefined') {
         return Promise.resolve();
      }

      const refreshPromise = (async () => {
         try {
            const response = await fetch(getLocaleAwareUrl('/cart'));
            if (!response.ok) return;
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newItems = doc.querySelector('.cart-items-container');
            if (newItems && this.cartItemsContainer) {
               this.cartItemsContainer.innerHTML = newItems.innerHTML;
            }
            const newEmpty = doc.querySelector('.empty-cart-message');
            if (newEmpty && this.emptyCartMessage) {
               this.emptyCartMessage.innerHTML = newEmpty.innerHTML;
            }
            if (cart) {
               this.updateItemDiscountTags(cart);
            }
         } catch (error) {
            console.warn('[CartDebug] refreshCartItemsMarkup failed', error);
         } finally {
            this.itemsMarkupRefreshInFlight = null;
         }
      })();

      this.itemsMarkupRefreshInFlight = refreshPromise;
      return refreshPromise;
   }

   updateCartMetricElements(cart) {
      const subtotalCents = typeof cart?.original_total_price === 'number' ? cart.original_total_price : 0;
      const totalDiscounts = CartCalculations.getTotalDiscountAmount(cart);
      const netSubtotal = Math.max(0, subtotalCents - totalDiscounts);

      // Effective cart total AFTER discounts; prefer Shopify's `total_price`
      // and fall back to our computed net subtotal.
      const effectiveTotalCents =
         typeof cart?.total_price === 'number' ? cart.total_price : netSubtotal;

      if (this.totalPrice) {
         this.totalPrice.textContent = CurrencyUtils.formatMoney(effectiveTotalCents);
      }

      window.updateFreeShippingBar?.(effectiveTotalCents);
      window.updateFreeGiftBar?.(effectiveTotalCents);
      window.refreshShippingRates?.();
   }

   toggleCartVisibility(itemCount) {
      if (itemCount === 0) {
         if (this.cartItemsContainer) {
         this.cartItemsContainer.style.display = 'none';
         }
         if (this.emptyCartMessage) {
         this.emptyCartMessage.style.display = 'block';
         }
      } else {
         if (this.cartItemsContainer) {
         this.cartItemsContainer.style.display = 'grid';
         }
         if (this.emptyCartMessage) {
         this.emptyCartMessage.style.display = 'none';
         }
      }
   }

   updateCartDisplay(cart) {
      if (!cart) return;

      this.lastKnownCart = cart;
      this.refreshElementReferences();

      console.log('[CartDebug] updateCartDisplay start', {
         original_total_price: cart.original_total_price,
         total_price: cart.total_price,
         item_count: cart.item_count,
         currency: getCurrentCurrency()
      });

      const subtotalCents = typeof cart.original_total_price === 'number' ? cart.original_total_price : 0;
      // Update data-cart-subtotal on the total element (which now has both attributes)
      if (this.totalPrice) {
         this.totalPrice.dataset.cartSubtotal = subtotalCents / 100;
      }
      // Also update cartSubtotal if it exists (for backwards compatibility)
      if (this.cartSubtotal) {
         this.cartSubtotal.dataset.cartSubtotal = subtotalCents / 100;
         this.cartSubtotal.textContent = CurrencyUtils.formatMoney(subtotalCents);
         console.log('[CartDebug] updateCartDisplay subtotal DOM updated', {
            datasetCartSubtotal: this.cartSubtotal.dataset.cartSubtotal,
            text: this.cartSubtotal.textContent
         });
      }

      this.updateCartMetricElements(cart);
      this.renderDiscountRows(cart);
      this.toggleCartVisibility(cart.item_count);

      this.updateItemDiscountTags(cart);
      this.refreshCartItemsMarkup(cart);

      window.syncCartCountBadge?.(cart);

      console.log('[CartDebug] updateCartDisplay end');
   }
}

// Initialize cart manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
   // Only initialize cart handlers on the cart page
   if (document.querySelector('.cart-items-container')) {
      new CartManager();
   }
}); 